import { useMutation, useQuery } from '@apollo/client/react';
import { useCallback, useEffect, useState } from 'react';
import { accountingCurrencyForCountry, Permission } from '@insio/shared-types';
import { hasCompanyRegistry } from '@insio/validation';
import { CountryCode, normalizeCountryCode } from '@insio/countries';
import { GetOnboardingOrganizationDocument, UpdatePartyDocument } from '@/graphql';
import type { CurrencyCode } from '@/graphql';
import { usePermissions } from '@/features/auth';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useCompanyRegistryFill } from '@/hooks/use-company-registry-fill';
import { partyErrorMessage } from '@/utils/party-issues';

/** `skip` means there is nothing to collect and the step should advance itself. */
export type OrganizationStepPhase = 'deciding' | 'ready' | 'skip';

/**
 * State for the onboarding organization step.
 * Skipped without `account.settings` (which `updateAccount` requires) or without a main party.
 */
export function useOrganizationStep() {
  const t = useFeatureTranslation('onboarding.organization');
  const canEditAccount = usePermissions().hasPermission(Permission.ACCOUNT_SETTINGS);

  // The hook only mounts while the step is on screen, so the query is scoped to it.
  const { data, loading: accountLoading } = useQuery(GetOnboardingOrganizationDocument, {
    skip: !canEditAccount
  });
  const mainParty = data?.currentAccount?.mainParty;
  const partyId = mainParty?.id ?? null;
  const storedCountry = mainParty?.mainAddress?.countryCode;

  const [updateParty] = useMutation(UpdatePartyDocument);
  const [country, setCountry] = useState<CountryCode | ''>('');
  const [countryError, setCountryError] = useState<string | null>(null);
  const [savingCountry, setSavingCountry] = useState(false);

  // Prefill once the account resolves, so a user who already has a country set
  // isn't asked for it again.
  useEffect(() => {
    if (storedCountry) setCountry(storedCountry);
  }, [storedCountry]);

  const registryCountry = hasCompanyRegistry(country) ? country : null;

  const registryFill = useCompanyRegistryFill({ countryCode: registryCountry, partyId });

  const changeCountry = useCallback((code: string) => {
    setCountry(normalizeCountryCode(code) ?? '');
    setCountryError(null);
  }, []);

  /** Persists the chosen country. Returns false when it could not be saved. */
  const saveCountry = useCallback(async (): Promise<boolean> => {
    if (!country) {
      setCountryError(t('errors.countryRequired'));
      return false;
    }
    if (!partyId) return true;

    setSavingCountry(true);
    try {
      const response = await updateParty({
        // Founding the account: the country also sets its accounting currency.
        variables: {
          id: partyId,
          input: {
            mainAddress: { countryCode: country },
            // A supported ISO code: the shared constant is typed as a plain string.
            accountingCurrencyCode: accountingCurrencyForCountry(country) as CurrencyCode
          }
        }
      });
      const result = response.data?.updateParty.result;
      if (result && result.errorCount > 0) {
        setCountryError(partyErrorMessage(result.issues) || t('errors.countrySaveFailed'));
        return false;
      }
      return true;
    } catch (e) {
      setCountryError(t('errors.countrySaveFailed'));
      // eslint-disable-next-line no-console
      console.warn('Failed to save the organization country:', e);
      return false;
    } finally {
      setSavingCountry(false);
    }
  }, [country, partyId, t, updateParty]);

  const phase: OrganizationStepPhase = !canEditAccount
    ? 'skip'
    : accountLoading
      ? 'deciding'
      : partyId
        ? 'ready'
        : 'skip';

  return {
    t,
    phase,
    country,
    changeCountry,
    countryError,
    savingCountry,
    saveCountry,
    /** Set when the chosen country has a business register we can query. */
    registryCountry,
    registryLoading: registryFill.loading,
    registryError: registryFill.error,
    registryApplied: registryFill.applied,
    applyRegistryLookup: registryFill.apply,
    clearRegistryFeedback: registryFill.clearFeedback
  };
}
